/**
 * API Error Interceptor
 * 
 * Handles API errors consistently across the application:
 * - 401: Redirect to login
 * - 403: Permission denied
 * - 5xx: Server errors with retry option
 * - Network errors: Connectivity issues
 * 
 * Validates: Requirements 8.1, 8.2
 */

let snackbarCallback = null;

/**
 * Register a callback to display error messages
 * This should be called from the GlobalSnackbar component
 */
export const registerSnackbarCallback = (callback) => {
    snackbarCallback = callback;
};

/**
 * Display error message to user
 */
const showError = (message, options = {}) => {
    if (snackbarCallback) {
        snackbarCallback(message, { severity: 'error', ...options });
    } else {
        // Fallback to console if snackbar not available
        console.error('API Error:', message);
    }
};

/**
 * Get user-friendly error message based on error type
 */
const getErrorMessage = (error) => {
    // Network error (no response)
    if (!error.response) {
        if (error.code === 'ECONNABORTED') {
            return 'Request timeout. Please check your connection and try again.';
        }
        if (error.message === 'Network Error') {
            return 'Network error. Please check your internet connection.';
        }
        return 'Unable to connect to server. Please try again later.';
    }

    const { status, data } = error.response;

    // Use server-provided message if available
    if (data?.message) {
        return data.message;
    }

    // Default messages based on status code
    switch (status) {
        case 400:
            return 'Invalid request. Please check your input.';
        case 401:
            return 'Your session has expired. Please login again.';
        case 403:
            return 'You do not have permission to perform this action.';
        case 404:
            return 'The requested resource was not found.';
        case 409:
            return 'This action conflicts with existing data.';
        case 422:
            return 'Validation error. Please check your input.';
        case 429:
            return 'Too many requests. Please wait a moment and try again.';
        case 500:
            return 'Server error. Please try again later.';
        case 502:
            return 'Bad gateway. The server is temporarily unavailable.';
        case 503:
            return 'Service unavailable. Please try again later.';
        case 504:
            return 'Gateway timeout. The server took too long to respond.';
        default:
            if (status >= 500) {
                return 'Server error. Please try again later.';
            }
            return 'An unexpected error occurred. Please try again.';
    }
};

/**
 * Check if error is retryable (5xx errors)
 */
const isRetryableError = (error) => {
    if (!error.response) {
        return true; // Network errors are retryable
    }
    const status = error.response.status;
    return status >= 500 && status < 600;
};

/**
 * Handle 401 Unauthorized - redirect to login
 */
const handle401 = () => {
    // Clear auth token
    localStorage.removeItem('user_token');
    
    // Redirect to appropriate login page
    const isAdminRoute = window.location.pathname.startsWith('/admin');
    const loginPath = isAdminRoute ? '/admin/login' : '/login';
    
    // Only redirect if not already on login page
    if (window.location.pathname !== loginPath) {
        window.location.href = loginPath;
    }
};

/**
 * Handle 403 Forbidden - show permission denied message
 */
const handle403 = () => {
    showError('You do not have permission to perform this action.');
};

/**
 * Handle 5xx Server Errors - show error with retry option
 */
const handle5xx = (error) => {
    const message = getErrorMessage(error);
    showError(message, {
        action: {
            label: 'Retry',
            onClick: () => {
                // The retry logic should be handled by the calling code
                // This just provides the UI feedback
                
            },
        },
    });
};

/**
 * Handle Network Errors - show connectivity message
 */
const handleNetworkError = (error) => {
    const message = getErrorMessage(error);
    showError(message, {
        duration: 6000, // Show longer for network errors
    });
};

/**
 * Main error interceptor function
 * This is called by Axios for all failed requests
 */
export const errorInterceptor = (error) => {
    // Log error for debugging
    console.error('API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message: error.message,
        data: error.response?.data,
    });

    // Handle different error types
    if (!error.response) {
        // Network error (no response from server)
        handleNetworkError(error);
    } else {
        const { status } = error.response;

        switch (status) {
            case 401:
                handle401();
                break;
            case 403:
                handle403();
                break;
            case 404:
                // 404 is often a valid response (empty list, resource not found)
                // Don't show error message - let the calling code handle it
                
                break;
            default:
                if (status >= 500) {
                    handle5xx(error);
                } else {
                    // Client errors (4xx except 404) - show specific message
                    const message = getErrorMessage(error);
                    showError(message);
                }
        }
    }

    // Return enhanced error object
    return Promise.reject({
        ...error,
        userMessage: getErrorMessage(error),
        isRetryable: isRetryableError(error),
    });
};

/**
 * Export utility functions for testing and external use
 */
export {
    getErrorMessage,
    isRetryableError,
    showError,
};
